<h1 mat-dialog-title>New Appointment</h1>
<mat-dialog-content [class.saving]="saving">
  @if (saving) {
    <xui-loading-spinner></xui-loading-spinner>
  }
  @if (form !== null && !saving) {
    <form class="new-appointment-form" [formGroup]="form" [class.form-validated]="validate">
      <mat-form-field appearance="fill" class="w-100 compact mat-background-reset mb-3" [style.background-color]="selectedType?.displayStyle" subscriptSizing="dynamic">
        <mat-label>Appointment Type</mat-label>
        <mat-select [formControlName]="'appointmentType'" required>
          @for (type of appointmentTypes; track type) {
            <mat-option class="py-0" [value]="type.id" [style.background-color]="type.displayStyle">{{type.title}}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-divider class="section-divider"></mat-divider>
      <!-- Label only for non-patient appointment types. For patient appointments the title is
           inferred from the patient's name, so showing it here would just duplicate that. -->
      @if (isNonPatientType) {
        <mat-form-field appearance="fill" class="w-100">
          <mat-label>Label</mat-label>
          <input matInput [formControlName]="'title'" type="text" maxlength="100"/>
        </mat-form-field>
      }

      @if (saveError) {
        <div class="save-error w-100 mb-3">{{saveError}}</div>
      }

      <!-- Existing-patient type: search and select only. No "add new patient" escape hatch. -->
      @if (isExistingPatientType) {
        <div class="w-100">
          @if (!hasPatient) {
            <xui-mat-autocomplete placeholder="Search Patients" class="w-100" [searchObservable]="searchPatients"
              [alwaysShowAddCard]="false"
            [displayResultsCardTemplate]='patientsTemplate' (itemSelected)="patientSelected($event)"></xui-mat-autocomplete>
          }
          <ng-template #patientsTemplate let-patient="result" let-queryText="queryText">
            <div class="patients-search-results container">
              <div class="row">
                @if (!practiceSettings?.useFileNumberAsPatientProfileId) {
                  <div class="col-sm-4 p-1" [innerHtml]="patient.profileId.toString().padStart(4, '0') | textHighlight: queryText"></div>
                }
                @if (practiceSettings?.useFileNumberAsPatientProfileId) {
                  <div class="col-sm-4 p-1" [innerHtml]="patient.fileNumber | textHighlight: queryText"></div>
                }
                <div class="col-sm-8 p-1" [innerHtml]="patient.displayName | textHighlight: queryText"></div>
              </div>
              <div class="row">
                <div class="col-sm-4 p-1" [innerHtml]="patient.cell | textHighlight: queryText"></div>
                <div class="col-sm-4 p-1" [innerHtml]="patient.tel | textHighlight: queryText"></div>
                <div class="col-sm-4 p-1" [innerHtml]="patient.identityNumber | textHighlight: queryText"></div>
              </div>
            </div>
          </ng-template>
          @if (!hasPatient) {
            <div class="patient-mode-hint w-100 mb-3">
              Booking a new patient? Choose a new-patient appointment type to register them.
            </div>
          }
          @if (hasPatient) {
            <ng-container [ngTemplateOutlet]="selectedPatient"></ng-container>
          }
        </div>
      }

      <!-- New-patient type: inline capture form, no search. Dedupe catches anyone who already exists here. -->
      @if (isNewPatientType) {
        @if (hasPatient) {
          <ng-container [ngTemplateOutlet]="selectedPatient"></ng-container>
        } @else {
          @if (hasDuplicates) {
            <div class="duplicate-patients w-100 mb-3">
              <div class="d-flex flex-row align-items-center justify-content-between">
                <span>{{duplicates.length}} existing patient(s) match this identity number. Select one to avoid creating a duplicate.</span>
                <button mat-icon-button type="button" (click)="toggleDuplicatesOpen()">
                  <mat-icon class="md-20">{{duplicatesOpen ? 'arrow_drop_up' : 'arrow_drop_down'}}</mat-icon>
                </button>
              </div>
              @if (duplicatesOpen) {
                @for (duplicate of duplicates; track duplicate.id) {
                  <div class="d-flex align-items-center justify-content-between py-1">
                    <span class="fw-bold">{{duplicate.profileId}} {{duplicate.displayName}} {{duplicate.identityNumber}}</span>
                    <button mat-stroked-button color="primary" type="button" (click)="useExistingPatient(duplicate)">Use</button>
                  </div>
                }
              }
            </div>
          }
          <div class="new-patient-form w-100" [formGroup]="newPatientForm" [class.form-validated]="newPatientValidate">
            <h5 class="mb-2">New Patient</h5>
            @if (allowPatientAppointmentsWithoutPatient) {
              <mat-slide-toggle class="w-100 mb-3 new-patient-mode-toggle"
                [checked]="newPatientSaveMode === 'quick'"
                [disabled]="newPatientLocked"
                (change)="applySaveMode($event.checked ? 'quick' : 'record')">
                <span class="toggle-title">Temporary patient only</span>
                <span class="toggle-subtext">Don't create a patient record yet</span>
              </mat-slide-toggle>
            }
            <div class="row">
              <div class="col-12 col-md-3">
                <mat-form-field appearance="fill" class="w-100">
                  <mat-label>Title</mat-label>
                  <mat-select formControlName="title" required>
                    @for (option of patientTitleOptions; track option.value) {
                      <mat-option [value]="option.value">{{option.text}}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
              <div class="col-12 col-md-4">
                <mat-form-field appearance="fill" class="w-100">
                  <mat-label>First Name</mat-label>
                  <input matInput formControlName="firstName" type="text"/>
                </mat-form-field>
              </div>
              <div class="col-12 col-md-5">
                <mat-form-field appearance="fill" class="w-100">
                  <mat-label>Surname</mat-label>
                  <input matInput formControlName="surname" type="text"/>
                </mat-form-field>
              </div>
            </div>
            <!-- ID block only for a real patient record; a temporary patient skips it. -->
            @if (newPatientSaveMode === 'record') {
              <div class="row">
                <div class="col-12 col-md-6">
                  <mat-form-field appearance="fill" class="w-100">
                    <mat-label>Identity No</mat-label>
                    <input matInput formControlName="identityNumber" type="text"/>
                  </mat-form-field>
                </div>
                <div class="col-12 col-md-6">
                  <mat-form-field appearance="fill" class="w-100">
                    <mat-label>Confirm Identity No</mat-label>
                    <input matInput formControlName="identityNumberConfirmation" type="text"/>
                  </mat-form-field>
                </div>
              </div>
            }
            <div formGroupName="cell">
              <mat-form-field appearance="fill" class="w-100">
                <mat-label>Cell Number</mat-label>
                <input matInput formControlName="number" type="text"/>
              </mat-form-field>
            </div>
            <div formGroupName="email">
              <mat-form-field appearance="fill" class="w-100">
                <mat-label>Email Address</mat-label>
                <input matInput formControlName="emailAddress" type="email"/>
              </mat-form-field>
            </div>
          </div>
        }
      }

      <ng-template #selectedPatient>
        <mat-form-field appearance="fill" class="w-100">
          <mat-label>Selected Patient</mat-label>
          <input readonly matInput [value]="(patient?.profileId ? patient.profileId.toString().padStart(4, '0') : '') + ' - ' + (patient?.displayName || '')">
          <button mat-icon-button matSuffix (click)="clearPatient()">
            <mat-icon>close</mat-icon>
          </button>
        </mat-form-field>
      </ng-template>
      <mat-divider class="section-divider"></mat-divider>
      @if (showLocationSelector) {
        <mat-form-field appearance="fill" class="w-100">
          <mat-label>Location</mat-label>
          <mat-select [formControlName]="'practiceLocationId'">
            @for (location of activeLocations; track location.id) {
              <mat-option [value]="location.id">{{location.name}}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      }
      <mat-form-field appearance="fill" class="w-100">
        <mat-label>Start Date & Time</mat-label>
        <xui-mat-datetime [formControlName]="'date'" #datetime="matDatetime" />
        <button mat-icon-button matSuffix (click)="datetime.togglePicker()">
          <mat-icon>today</mat-icon>
        </button>
      </mat-form-field>
      <mat-form-field appearance="fill" class="w-100">
        <mat-label>End Date & Time</mat-label>
        <xui-mat-datetime [formControlName]="'endDate'" #endDatetime="matDatetime" />
        <button mat-icon-button matSuffix (click)="endDatetime.togglePicker()">
          <mat-icon>today</mat-icon>
        </button>
      </mat-form-field>
      <app-appointment-clash-banner
        [locationId]="clashLocationId"
        [practitionerId]="clashPractitionerId"
        [start]="form.get('date')?.value"
        [end]="form.get('endDate')?.value"
        [appointmentTypeFiltersActive]="appointmentTypeFiltersActive">
      </app-appointment-clash-banner>
      <mat-divider class="section-divider"></mat-divider>
      <mat-form-field appearance="fill" class="w-100">
        <mat-label>Booking Comment</mat-label>
        <textarea matInput [formControlName]="'comment'" maxlength="250"></textarea>
      </mat-form-field>
    </form>
  }
</mat-dialog-content>
<mat-dialog-actions align="end" class="mx-3 mb-2">
  <button mat-button (click)="close()" [disabled]="saving">Cancel</button>
  @if (isNewPatientType && !hasPatient) {
    <button mat-flat-button color="primary" (click)="bookNewPatient()" [disabled]="saving">Book appointment</button>
  } @else {
    <button mat-flat-button color="primary" (click)="save()" [disabled]="saving || (isExistingPatientType && !hasPatient)">Save</button>
  }
</mat-dialog-actions>
